import { useMemo } from 'react';

import { CirclePlusIcon, Loader2 } from 'lucide-react';
import { useFieldArray, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatAmountWithCurrencyCode, parseFormNumber } from '@/helpers/finance';
import { cn } from '@/lib/utils';
import { CommonConstants } from '@/modules/common/constants/common.constants';
import { RateMask } from '@/modules/common/utils/rate-mask';
import {
  SalaryAdjustmentLineLabel,
  type SalaryAdjustmentLineType,
  SalaryAdjustmentLineTypes,
} from '@/modules/finance/common';

import { BillDetails, BillDetailsItemType, BillMonthlySalaryAvailability } from '../../types';
import {
  calculateSalaryAdjustment,
  groupBillLineItemIndexesByType,
  sumReconciledLineAmounts,
} from '../../utils';
import {
  BillValidationIssueCode,
  getBillLineValidationIssues,
  getBillValidationIssues,
  UPDATE_BILL_MESSAGE_BY_ISSUE,
  UpdateBillFormValues,
} from '../../validations';
import { BillLineItemRow } from './bill-line-item-row';

const EMPTY_LINE_ITEMS_ERROR_MESSAGE =
  UPDATE_BILL_MESSAGE_BY_ISSUE[BillValidationIssueCode.EmptyItems];

interface BillLineItemsCardProps {
  details: BillDetails;
  form: UseFormReturn<UpdateBillFormValues>;
  isReadOnly: boolean;
  currencyCode: string;
  monthlySalaryAvailability: BillMonthlySalaryAvailability | undefined;
  isMonthlySalaryAvailabilityLoading: boolean;
}

/** Owns bill line-item editing and presentation; totals remain with the parent form. */
export function BillLineItemsCard({
  details,
  form,
  isReadOnly,
  currencyCode,
  monthlySalaryAvailability,
  isMonthlySalaryAvailabilityLoading,
}: BillLineItemsCardProps) {
  const { t } = useTranslation('authenticated.bills');
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'items' });
  const items = useWatch({ control: form.control, name: 'items' });
  const isMonthlySalaryEnabled = useWatch({ control: form.control, name: 'monthlySalaryEnabled' });
  const groupedLineItemIndexes = groupBillLineItemIndexesByType(fields);
  const monthlySalaryAmount = monthlySalaryAvailability?.monthlySalaryAmount ?? null;
  const isMonthlySalaryAmountMasked = RateMask.isMaskedStringValue(
    monthlySalaryAmount ?? undefined
  );
  const isMonthlySalaryAvailable = Boolean(
    monthlySalaryAvailability?.isAvailable && monthlySalaryAmount
  );
  const availableTimeOffTypesCount = details.timeOffSources.filter(
    timeOff => timeOff.isBillable
  ).length;
  const currentTimeOffLinesCount = items.filter(
    item => item.entityType === BillDetailsItemType.TimeOffType
  ).length;
  const reconciledItems = items.filter(
    item =>
      item.entityType === BillDetailsItemType.Project ||
      item.entityType === BillDetailsItemType.TimeOffType
  );
  const hasMaskedReconciledValues = reconciledItems.some(
    item =>
      RateMask.isMaskedStringValue(item.quantity) || RateMask.isMaskedStringValue(item.unitPrice)
  );
  const canCalculateMonthlySalaryAdjustment =
    Boolean(monthlySalaryAmount) && !isMonthlySalaryAmountMasked && !hasMaskedReconciledValues;
  const adjustmentAmount = canCalculateMonthlySalaryAdjustment
    ? calculateSalaryAdjustment({
        monthlySalaryAmount: parseFormNumber(monthlySalaryAmount),
        reconciledLinesTotal: sumReconciledLineAmounts(
          reconciledItems.map(item => ({
            hours: parseFormNumber(item.quantity),
            rate: parseFormNumber(item.unitPrice),
          }))
        ),
      })
    : 0;
  const lineValidationIssues = useMemo(
    () => (isReadOnly ? [] : items.map(getBillLineValidationIssues)),
    [isReadOnly, items]
  );
  const isEmptyLineItemsError =
    !isReadOnly &&
    getBillValidationIssues({
      items,
      monthlySalaryEnabled: isMonthlySalaryEnabled,
    }).includes(BillValidationIssueCode.EmptyItems);
  const shouldShowSalaryLine = isMonthlySalaryEnabled && items.length === 0 && monthlySalaryAmount;
  const shouldShowAdjustmentLine = isMonthlySalaryEnabled && items.length > 0;

  const addLine = (entityType: BillDetailsItemType) =>
    append({
      entityType,
      entityId: '',
      description: '',
      quantity: entityType === BillDetailsItemType.Overhead ? '1' : '0',
      unitPrice: entityType === BillDetailsItemType.Overhead ? '' : '0',
      amount: '0',
    });

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-row items-center justify-between gap-4">
        <CardTitle>{t('details.lineItems')}</CardTitle>
        {!isReadOnly && (
          <MonthlySalaryField
            checked={isMonthlySalaryEnabled}
            disabled={!isMonthlySalaryAvailable || isMonthlySalaryAvailabilityLoading}
            isLoading={isMonthlySalaryAvailabilityLoading}
            onCheckedChange={checked =>
              form.setValue('monthlySalaryEnabled', checked, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />
        )}
      </CardHeader>
      <div className="overflow-x-auto">
        <Table className="min-w-[980px] table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[250px] min-w-[250px]">
                {t('details.lineItemsHeaders.name')}
              </TableHead>
              <TableHead className="w-56">{t('details.lineItemsHeaders.hoursSource')}</TableHead>
              <TableHead className="w-40">{t('details.lineItemsHeaders.quantity')}</TableHead>
              <TableHead className="w-56">{t('details.lineItemsHeaders.rate')}</TableHead>
              <TableHead className="w-40">{t('headers.amount')}</TableHead>
              {!isReadOnly && <TableHead className="w-16" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupedLineItemIndexes.map(index => {
              const field = fields[index];
              const detailsItem = details.items.find(item => item.id === field.sourceItemId);

              return (
                <BillLineItemRow
                  key={field.id}
                  index={index}
                  itemName={detailsItem?.name}
                  itemAvatarUrl={detailsItem?.avatarUrl}
                  itemColorHex={detailsItem?.colorHex}
                  timeOffSources={details.timeOffSources}
                  form={form}
                  isReadOnly={isReadOnly}
                  currencyCode={currencyCode}
                  validationIssues={lineValidationIssues[index] ?? []}
                  onRemove={() => remove(index)}
                />
              );
            })}
            {shouldShowSalaryLine && (
              <DerivedBillLineItemRow
                type={SalaryAdjustmentLineTypes.Salary}
                amount={monthlySalaryAmount}
                currencyCode={currencyCode}
                hasActions={!isReadOnly}
                isAmountMasked={isMonthlySalaryAmountMasked}
              />
            )}
            {shouldShowAdjustmentLine && (
              <DerivedBillLineItemRow
                type={SalaryAdjustmentLineTypes.Adjustment}
                amount={adjustmentAmount.toString()}
                currencyCode={currencyCode}
                hasActions={!isReadOnly}
                isAmountMasked={!canCalculateMonthlySalaryAdjustment}
              />
            )}
            {!isReadOnly && (
              <TableRow>
                <TableCell colSpan={6} className="py-2 pl-6">
                  <AddBillLineButton
                    label={t('details.edit.items.addLine')}
                    onAddProject={() => addLine(BillDetailsItemType.Project)}
                    onAddTimeOff={() => addLine(BillDetailsItemType.TimeOffType)}
                    onAddOverhead={() => addLine(BillDetailsItemType.Overhead)}
                    canAddTimeOff={currentTimeOffLinesCount < availableTimeOffTypesCount}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {isEmptyLineItemsError && (
        <p className="px-6 pb-4 text-sm font-medium text-destructive">
          {t(EMPTY_LINE_ITEMS_ERROR_MESSAGE)}
        </p>
      )}
    </Card>
  );
}

function AddBillLineButton({
  label,
  onAddProject,
  onAddTimeOff,
  onAddOverhead,
  canAddTimeOff,
}: {
  label: string;
  onAddProject: () => void;
  onAddTimeOff: () => void;
  onAddOverhead: () => void;
  canAddTimeOff: boolean;
}) {
  const { t } = useTranslation('authenticated.bills');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" className="h-9 gap-2 px-3">
          <CirclePlusIcon size={16} />
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={onAddProject}>
          {t('lineItemActions.addProject')}
        </DropdownMenuItem>
        {canAddTimeOff && (
          <DropdownMenuItem onClick={onAddTimeOff}>
            {t('details.edit.items.addTimeOff')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={onAddOverhead}>
          {t('lineItemActions.addOverhead')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MonthlySalaryField({
  checked,
  disabled,
  isLoading,
  onCheckedChange,
}: {
  checked: boolean;
  disabled: boolean;
  isLoading: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation('authenticated.bills', { keyPrefix: 'monthlySalary' });
  const { t: tCommon } = useTranslation('common');

  const field = (
    <Label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
      <span>{t('label')}</span>
      <span className="relative inline-flex h-6 w-11 items-center justify-center">
        <Switch
          checked={checked}
          disabled={disabled}
          onCheckedChange={onCheckedChange}
          className={cn(isLoading && 'opacity-50')}
        />
        {isLoading && (
          <Loader2
            className="pointer-events-none absolute h-4 w-4 animate-spin text-muted-foreground"
            aria-label={tCommon('loading')}
          />
        )}
      </span>
    </Label>
  );

  if (!disabled || isLoading) return field;

  return (
    <SimpleTooltip content={t('ineligibleTooltip')} contentClassName="max-w-xs">
      <span className="inline-flex cursor-help">{field}</span>
    </SimpleTooltip>
  );
}

function DerivedBillLineItemRow({
  type,
  amount,
  currencyCode,
  hasActions,
  isAmountMasked,
}: {
  type: SalaryAdjustmentLineType;
  amount: string;
  currencyCode: string;
  hasActions: boolean;
  isAmountMasked: boolean;
}) {
  const { t } = useTranslation('authenticated.bills');
  return (
    <TableRow className="bg-muted/40 text-muted-foreground">
      <TableCell>
        <div className="pl-1">
          <SalaryAdjustmentLineLabel type={type} />
        </div>
      </TableCell>
      <TableCell>{t('monthlySalary.adjustmentSourceLabel')}</TableCell>
      <TableCell>-</TableCell>
      <TableCell>-</TableCell>
      <TableCell
        className={cn('font-semibold', Number.parseFloat(amount) < 0 && 'text-destructive')}
      >
        {isAmountMasked
          ? RateMask.apply(CommonConstants.MASKED_RATE)
          : formatAmountWithCurrencyCode(amount, currencyCode)}
      </TableCell>
      {hasActions && <TableCell />}
    </TableRow>
  );
}
